import React, { useState, useEffect, useCallback } from 'react';
import SelectField from '../common/SelectField.js';
import { useParams, useNavigate } from 'react-router-dom';
import { locationHubService } from '../../services/locationHubService';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import { ArrowLeft, MapPin} from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import StateSelectField from '../common/StateSelectField.js';
import { normalizeDestinationType } from '../../utils/destinationType.js';
import { hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import HubGpsFields, { fillCurrentGps } from './HubGpsFields.js';
import DestinationLocationHubField from './DestinationLocationHubField.js';

const EditLocationHub = () => {
  const { hubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showDispatchControls = user?.role === 'System Admin' || hasInternalHubDispatchControls(user);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [hubData, setHubData] = useState(null);
  const [parentHubOptions, setParentHubOptions] = useState([]);
  const [destinationHubOptions, setDestinationHubOptions] = useState([]);
  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    address:                (v) => validators.required(v, 'Address'),
    location:               (v) => validators.required(v, 'Hub/Branch'),
    state:                  (v) => validators.required(v, 'State'),
    location_code:          (v) => validators.required(v, 'Location code'),
    location_hub_contact:   (v) => v ? validators.contactVarchar(v) : '',
    commission_percentage:  (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Commission' }) : '',
    rate_per_kg:            (v) => v ? validators.number(v, { min: 0, label: 'Rate / KG' }) : '',
    monthly_revenue_target: (v) => v ? validators.number(v, { min: 0, label: 'Monthly revenue target' }) : '',
    parent_hub_id:          () => '',
    dispatch_types:         (v) => showDispatchControls && (!Array.isArray(v) || v.length === 0)
      ? 'Select at least one dispatch type'
      : '',
  };

  const [formData, setFormData] = useState({
    address: '',
    location: '',
    state: '',
    location_code: '',
    destination_type: '',
    parent_hub_id: '',
    location_hub_contact: '',
    commission_percentage: '',
    rate_per_kg: '',
    monthly_revenue_target: '',
    ownership_type: 'OWN',
    dispatch_types: ['OGCL'],
    allow_paid: true,
    allow_to_pay: true,
    allow_on_account: true,
    allow_delivery: true,
    allow_booking: true,
    latitude: '',
    longitude: '',
    destination_location_hub_ids: [],
  });

  const isBranch = formData.destination_type === 'branch_destination';

  const loadParentHubs = useCallback(async (companyPk, excludeHubPk) => {
    try {
      const filters = { destination_type: 'hub_destination' };
      if (companyPk) filters.company_id = companyPk;
      const response = await locationHubService.getHubsForSelect(filters);
      let hubs = response.data.hubs || response.data.data || [];
      if (excludeHubPk) {
        hubs = hubs.filter((hub) => String(hub.id) !== String(excludeHubPk));
      }
      setParentHubOptions(hubs);
    } catch (error) {
      console.error('Error loading parent hubs:', error);
      setParentHubOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchHub();
  }, [hubId]);

  const fetchHub = async () => {
    try {
      setLoading(true);
      const response = await locationHubService.getHub(hubId);
      const hub = response.data.hub || response.data.data;
      
      if (hub) {
        setHubData(hub);
        setFormData({
          address: hub.address || '',
          location: hub.location || '',
          state: hub.state || '',
          location_code: hub.location_code || '',
          destination_type: normalizeDestinationType(hub.destination_type) || 'branch_destination',
          parent_hub_id: hub.parent_hub_id && String(hub.parent_hub_id) !== String(hub.id)
            ? String(hub.parent_hub_id)
            : '',
          location_hub_contact: hub.location_hub_contact || '',
          commission_percentage: hub.commission_percentage !== null && hub.commission_percentage !== undefined ? hub.commission_percentage : '',
          rate_per_kg: hub.rate_per_kg !== null && hub.rate_per_kg !== undefined ? hub.rate_per_kg : '',
          monthly_revenue_target: hub.monthly_revenue_target !== null && hub.monthly_revenue_target !== undefined ? hub.monthly_revenue_target : '',
          ownership_type: hub.ownership_type || 'OWN',
          dispatch_types: Array.isArray(hub.dispatch_types)
            ? hub.dispatch_types
            : (hub.dispatch_types ? String(hub.dispatch_types).split(',').map((type) => type.trim()).filter(Boolean) : ['OGCL']),
          allow_paid: hub.allow_paid !== false,
          allow_to_pay: hub.allow_to_pay !== false,
          allow_on_account: hub.allow_on_account !== false,
          allow_delivery: hub.allow_delivery !== false,
          allow_booking: hub.allow_booking !== false,
          latitude: hub.latitude != null ? String(hub.latitude) : '',
          longitude: hub.longitude != null ? String(hub.longitude) : '',
          destination_location_hub_ids: Array.isArray(hub.destination_location_hub_ids)
            ? hub.destination_location_hub_ids
            : null,
        });
      } else {
        Alert.error('Location hub not found');
      }
    } catch (error) {
      console.error('Error fetching hub:', error);
      Alert.error(error.response?.data?.error || 'Failed to load location hub');
    } finally {
      setLoading(false);
    }
  };

  const handleDispatchTypeChange = (type) => {
    setFormData((prev) => {
      const dispatch_types = prev.dispatch_types.includes(type)
        ? prev.dispatch_types.filter((item) => item !== type)
        : [...prev.dispatch_types, type];
      validateField('dispatch_types', dispatch_types, validationRules.dispatch_types);
      return { ...prev, dispatch_types };
    });
  };

  const handleHubToggle = (name) => {
    setFormData((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  useEffect(() => {
    if (!hubData || !isBranch) {
      if (!isBranch) setParentHubOptions([]);
      return;
    }
    loadParentHubs(hubData.internal_company_id, hubData.id);
  }, [hubData, isBranch, loadParentHubs]);

  const loadDestinationHubs = useCallback(async (companyPk, excludeHubPk) => {
    try {
      const filters = {};
      if (companyPk) filters.company_id = companyPk;
      const response = await locationHubService.getHubsForSelect(filters);
      let hubs = response.data.hubs || response.data.data || [];
      if (excludeHubPk) {
        hubs = hubs.filter((hub) => String(hub.id) !== String(excludeHubPk));
      }
      setDestinationHubOptions(hubs);
      return hubs;
    } catch (error) {
      console.error('Error loading destination hubs:', error);
      setDestinationHubOptions([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!hubData) return;
    let cancelled = false;
    loadDestinationHubs(hubData.internal_company_id, hubData.id).then((hubs) => {
      if (cancelled) return;
      setFormData((prev) => {
        if (prev.destination_location_hub_ids == null) {
          return { ...prev, destination_location_hub_ids: hubs.map((hub) => hub.id) };
        }
        return prev;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [hubData, loadDestinationHubs]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'location_hub_contact') filtered = inputFilters.phoneVarchar(value);
    if (name === 'commission_percentage') filtered = inputFilters.amount(value);
    if (name === 'rate_per_kg') filtered = inputFilters.amount(value);
    if (name === 'monthly_revenue_target') filtered = inputFilters.amount(value);
    if (name === 'location' || name === 'location_code') filtered = value.toUpperCase();

    setFormData((prev) => {
      const next = { ...prev, [name]: filtered };
      if (name === 'destination_type' && filtered !== 'branch_destination') {
        next.parent_hub_id = '';
      }
      return next;
    });

    if (validationRules[name]) {
      const nextAll = {
        ...formData,
        [name]: filtered,
        ...(name === 'destination_type' && filtered !== 'branch_destination'
          ? { parent_hub_id: '' }
          : {}),
      };
      validateField(name, filtered, (v) => validationRules[name](v, nextAll));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, {
      ...validationRules,
      parent_hub_id: (v) => validationRules.parent_hub_id(v, formData),
    })) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    const confirmed = await Alert.confirm('Are you sure you want to update this location hub?', 'Update Location Hub');
    if (!confirmed) return;

    try {
      setUpdating(true);
      
      const submitData = {
        address: formData.address.trim(),
        location: formData.location.trim().toUpperCase(),
        state: formData.state,
        location_code: formData.location_code.trim().toUpperCase(),
        destination_type: formData.destination_type,
        parent_hub_id: isBranch
          && formData.parent_hub_id
          && String(formData.parent_hub_id) !== String(hubData?.id)
          ? parseInt(formData.parent_hub_id, 10)
          : null,
        destination_location_hub_ids: (formData.destination_location_hub_ids || [])
          .map((id) => parseInt(id, 10))
          .filter((id) => Number.isInteger(id)),
        location_hub_contact: formData.location_hub_contact
          ? String(formData.location_hub_contact).trim()
          : null,
        commission_percentage: formData.commission_percentage !== '' ? parseInt(formData.commission_percentage) : null,
        rate_per_kg: formData.rate_per_kg !== '' ? parseFloat(formData.rate_per_kg) : null,
        monthly_revenue_target: formData.monthly_revenue_target !== '' ? parseFloat(formData.monthly_revenue_target) : null,
        ...(showDispatchControls ? {
          ownership_type: formData.ownership_type,
          dispatch_types: formData.dispatch_types,
          allow_paid: formData.allow_paid,
          allow_to_pay: formData.allow_to_pay,
          allow_on_account: formData.allow_on_account,
          allow_delivery: formData.allow_delivery,
          allow_booking: formData.allow_booking,
        } : {}),
        latitude: formData.latitude === '' ? null : formData.latitude,
        longitude: formData.longitude === '' ? null : formData.longitude,
      };

      await locationHubService.updateHub(hubId, submitData);
      
      Alert.success('Location hub updated successfully!');
      navigate('/location-hubs');
      
    } catch (error) {
      console.error('Error updating hub:', error);
      Alert.error(error.response?.data?.error || 'Failed to update location hub');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading location hub..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!hubData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md text-center">
              <div className="text-6xl mb-4">❌</div>
              <p className="text-red-600 text-lg font-bold mb-2">Location Hub Not Found</p>
              <p className="text-gray-600 mb-4">The location hub you are looking for does not exist.</p>
              <button
                onClick={() => navigate('/location-hubs')}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition"
              >
                Back to Hubs
              </button>
            </div>
        </div>
      </DashboardLayout>
    );
  }

  const parentSelectOptions = parentHubOptions.filter(
    (hub) => String(hub.id) !== String(hubData?.id),
  );
  if (
    formData.parent_hub_id
    && !parentSelectOptions.some((hub) => String(hub.id) === String(formData.parent_hub_id))
  ) {
    parentSelectOptions.unshift({
      id: formData.parent_hub_id,
      location: hubData?.parent_hub_name || 'Current parent',
      location_code: hubData?.parent_hub_code || '',
    });
  }

  const selectedParent = parentSelectOptions.find(
    (hub) => String(hub.id) === String(formData.parent_hub_id)
  );

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                  {/* ROW: Title (left) + Button (right) */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                    {/* LEFT TITLE */}
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <MapPin className="w-6 h-6 text-secondary" />
                      Edit Destination Hub
                    </h1>

                    {/* RIGHT BUTTON */}
                    <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/location-hubs')}>
                      Back to Location Hubs
                    </Button>
                  </div>
                </div>
                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-6 sm:space-y-8">
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Hub Information</h3>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                    {/* Destination */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Hub/Branch <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Enter Destination name"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={updating}
                      />
                      <ValidationMessage error={errors.location} />
                    </div>

                    {/* Location Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Location Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location_code"
                        value={formData.location_code}
                        onChange={handleChange}
                        placeholder="e.g., MU for Mumbai"
                        maxLength="10"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base uppercase ${errors.location_code ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={updating}
                      />
                      <ValidationMessage error={errors.location_code} />
                    </div>

                    {/* Commission Percentage */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Commission %
                      </label>
                      <input
                        type="text"
                        name="commission_percentage"
                        value={formData.commission_percentage}
                        onChange={handleChange}
                        placeholder="0 - 100"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.commission_percentage ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={updating}
                      />
                      <ValidationMessage error={errors.commission_percentage} />
                    </div>

                    {/* Rate / KG */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Rate / KG (₹) <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        name="rate_per_kg"
                        value={formData.rate_per_kg}
                        onChange={handleChange}
                        placeholder="e.g. 4.5"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.rate_per_kg ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={updating}
                      />
                      <p className="mt-1 text-xs text-gray-400">Used for weight-based freight when this hub is the receiver</p>
                      <ValidationMessage error={errors.rate_per_kg} />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Monthly revenue target (₹) <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        name="monthly_revenue_target"
                        value={formData.monthly_revenue_target}
                        onChange={handleChange}
                        placeholder="e.g. 500000"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.monthly_revenue_target ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={updating}
                      />
                      <p className="mt-1 text-xs text-gray-400">Compared with revenue booked from this location</p>
                      <ValidationMessage error={errors.monthly_revenue_target} />
                    </div>

                    {/* Destination Type */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Destination Type <span className="text-red-500">*</span>
                      </label>
                      <SelectField variant="form"
                        name="destination_type"
                        value={formData.destination_type}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        disabled={updating}
                      >
                        <option value="branch_destination">Branch</option>
                        <option value="hub_destination">Hub</option>
                      </SelectField>
                    </div>

                    {/* Parent Hub — only for Branch */}
                    {isBranch && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Parent Hub <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <SelectField variant="form"
                          name="parent_hub_id"
                          value={formData.parent_hub_id}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.parent_hub_id ? 'border-red-400' : 'border-gray-300'}`}
                          disabled={updating}
                        >
                          <option value="">
                            {parentSelectOptions.length === 0 ? 'No hubs available' : 'Select parent hub'}
                          </option>
                          {parentSelectOptions.map((hub) => (
                            <option key={hub.id} value={hub.id}>
                              {hub.location} ({hub.location_code})
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.parent_hub_id} />
                        {selectedParent && (
                          <p className="mt-1 text-xs text-gray-500">
                            Parent location code: <span className="font-mono font-semibold">{selectedParent.location_code}</span>
                          </p>
                        )}
                      </div>
                    )}

                    <DestinationLocationHubField
                      hubs={destinationHubOptions}
                      selectedIds={formData.destination_location_hub_ids || []}
                      onChange={(ids) => setFormData((prev) => ({
                        ...prev,
                        destination_location_hub_ids: ids,
                      }))}
                      sourceLabel={formData.location.trim() || hubData.location || 'This location hub'}
                      sourceCode={formData.location_code.trim() || hubData.location_code}
                      disabled={updating}
                    />

                    {/* Address */}
                    <div className="col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Enter complete address"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.address ? 'border-red-400' : 'border-gray-300'}`}
                          required
                          disabled={updating}
                        />
                        <ValidationMessage error={errors.address} />
                      </div>
                    </div>

                    <HubGpsFields
                      formData={formData}
                      onChange={handleChange}
                      loading={updating}
                      onUseCurrent={() => fillCurrentGps((coords) => setFormData((prev) => ({ ...prev, ...coords })))}
                    />

                    {showDispatchControls && (
                      <div className="col-span-2 sm:col-span-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <h4 className="font-semibold text-secondary mb-3">Hub Dispatch Capabilities</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Ownership Type</label>
                            <SelectField variant="form" name="ownership_type" value={formData.ownership_type} onChange={handleChange} disabled={updating} className="w-full px-3 py-2 border border-gray-300 rounded-xl">
                              <option value="OWN">Own</option>
                              <option value="AGENT">Agent</option>
                            </SelectField>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-xs sm:text-sm font-medium text-secondary mb-2">Dispatch Types <span className="text-red-500">*</span></p>
                            <div className="flex gap-5">
                              {['OGCL', 'ITCL'].map((type) => (
                                <label key={type} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={formData.dispatch_types.includes(type)} onChange={() => handleDispatchTypeChange(type)} disabled={updating} />
                                  {type}
                                </label>
                              ))}
                            </div>
                            <ValidationMessage error={errors.dispatch_types} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                          {[
                            ['allow_paid', 'Allow Paid'],
                            ['allow_to_pay', 'Allow To Pay'],
                            ['allow_on_account', 'Allow On Account'],
                            ['allow_delivery', 'Allow Delivery'],
                            ['allow_booking', 'Allow Booking'],
                          ].map(([name, label]) => (
                            <label key={name} className="flex items-center gap-2 text-sm text-gray-700">
                              <input type="checkbox" checked={formData[name]} onChange={() => handleHubToggle(name)} disabled={updating} />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <StateSelectField
                      label="State"
                      required
                      disabled={updating}
                      value={formData.state}
                      error={errors.state}
                      onChange={(state) => {
                        setFormData({ ...formData, state });
                        validateField('state', state, validationRules.state);
                      }}
                    />

                    {/* Contact */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Contact No.
                      </label>
                      <input
                        type="text"
                        name="location_hub_contact"
                        value={formData.location_hub_contact}
                        onChange={handleChange}
                        placeholder="Enter contact number"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location_hub_contact ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={updating}
                      />
                      <ValidationMessage error={errors.location_hub_contact} />
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" variant="primary" disabled={updating} loading={updating}>
                      {updating ? 'Updating...' : 'Update Hub'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/location-hubs')} disabled={updating}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default EditLocationHub;
